import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import { GraphicsError } from '@/graphics/GraphicsErrors'
import type { GSplatCapability } from '@/shared/types/gsplatCapability'

export type GSplatCapabilityOperation = 'gpu-sort' | 'gpu-picking'

/** 3DGS 在开始资源加载或请求入队前发现的私有能力缺口。 */
export class GSplatCapabilityRequirementsError extends GraphicsError {
  readonly operation: GSplatCapabilityOperation
  readonly backend: GraphicsBackendKind
  readonly missingRequirements: readonly string[]

  constructor(
    operation: GSplatCapabilityOperation,
    backend: GraphicsBackendKind,
    missingRequirements: readonly string[]
  ) {
    const frozenMissing = Object.freeze([...missingRequirements])
    super(
      'GSPLAT_CAPABILITY_REQUIREMENTS_NOT_MET',
      `3DGS ${operation} 能力不满足（backend=${backend}）：${frozenMissing.join(', ')}`
    )
    this.operation = operation
    this.backend = backend
    this.missingRequirements = frozenMissing
  }
}

function requirePositiveFinite(missing: string[], name: string, value: number): void {
  if (!Number.isFinite(value) || value <= 0) missing.push(`${name}>0`)
}

/** resolved GPU sorting 的完整 compute/storage/indirect 基线。 */
export function assertGSplatGpuSortingRequirements(
  capabilities: GSplatCapability,
  backend: GraphicsBackendKind
): void {
  const missing: string[] = []
  if (!capabilities.supported.includes('gpu-sort')) missing.push('capability:gpu-sort')

  const compute = capabilities.gpuCompute
  if (!compute.compute) missing.push('compute')
  if (!compute.storageBuffers) missing.push('storage-buffers')
  if (!compute.storageTextures) missing.push('storage-textures')
  if (!compute.atomicUint32) missing.push('atomic-uint32')
  if (!compute.indirectDispatch) missing.push('indirect-dispatch')
  if (!compute.indirectDraw) missing.push('indirect-draw')
  requirePositiveFinite(missing, 'maxStorageBufferBindingSize', compute.maxStorageBufferBindingSize)
  requirePositiveFinite(missing, 'maxBufferSize', compute.maxBufferSize)
  requirePositiveFinite(
    missing,
    'maxStorageBuffersPerShaderStage',
    compute.maxStorageBuffersPerShaderStage
  )
  requirePositiveFinite(
    missing,
    'maxComputeWorkgroupStorageSize',
    compute.maxComputeWorkgroupStorageSize
  )
  requirePositiveFinite(
    missing,
    'maxComputeInvocationsPerWorkgroup',
    compute.maxComputeInvocationsPerWorkgroup
  )
  requirePositiveFinite(missing, 'maxComputeWorkgroupSizeX', compute.maxComputeWorkgroupSizeX)
  requirePositiveFinite(
    missing,
    'maxComputeWorkgroupsPerDimension',
    compute.maxComputeWorkgroupsPerDimension
  )
  requirePositiveFinite(missing, 'radixScratchBytes', compute.radixScratchBytes)

  if (missing.length > 0) {
    throw new GSplatCapabilityRequirementsError('gpu-sort', backend, missing)
  }
}

/** 异步 GPU picking 的插件私有 target/readback 基线。 */
export function assertGSplatGpuPickingRequirements(
  capabilities: GSplatCapability,
  backend: GraphicsBackendKind
): void {
  const missing: string[] = []
  if (!capabilities.supported.includes('gpu-picking')) {
    missing.push('capability:gpu-picking')
  }
  if (!capabilities.gpuPicking.privateTarget) missing.push('private-target')
  if (!capabilities.gpuPicking.targetFormats.includes('rgba8unorm')) {
    missing.push('target-format:rgba8unorm')
  }
  if (!capabilities.gpuPicking.asyncReadback) missing.push('async-readback')
  requirePositiveFinite(missing, 'maxPickIds', capabilities.gpuPicking.maxPickIds)

  if (missing.length > 0) {
    throw new GSplatCapabilityRequirementsError('gpu-picking', backend, missing)
  }
}
